import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { 
  Users, Plus, Search, Edit2, Trash2, X, Save, Loader2,
  Building2, Phone, Mail, MapPin, UserCheck, UserX,
  Eye, Key, Lock, ChevronRight, ArrowLeft, Shield,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Wallet, Percent,
  Upload, Download, FileText, Tag, Power, MoreVertical, CheckCircle2, AlertCircle, ChevronDown, Filter, MapPin as RegionIcon, FileSpreadsheet, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatCurrency = (v) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(v || 0);
const formatDate = (d) => d ? new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

function DealerAccountMovements({ dealerId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderLoading, setOrderLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/dealers/${dealerId}/account-movements`, { headers: { Authorization: `Bearer ${token}` } });
        setData(res.data);
      } catch { toast.error('Cari hareketler yuklenemedi'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [dealerId, token]);

  const openOrderDetail = async (orderId) => {
    if (!orderId) return;
    setOrderLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/orders/${orderId}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedOrder(res.data);
    } catch { toast.error('Sipariş detayı alınamadı'); }
    finally { setOrderLoading(false); }
  };

  const downloadPdf = async (order) => {
    try {
      const res = await axios.get(`${API_URL}/api/orders/${order.id}/pdf`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `siparis_${order.order_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success('PDF indirildi');
    } catch { toast.error('PDF indirilemedi'); }
  };

  const downloadExport = async (format) => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/dealers/${dealerId}/account-movements/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const mime = format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cari_hareketler_${new Date().toISOString().slice(0,10)}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(format === 'pdf' ? 'PDF indirildi' : 'Excel indirildi');
    } catch { toast.error('Dışa aktarma başarısız'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!data) return null;

  const STATUS_LABEL = { pending: 'Beklemede', preparing: 'Hazırlanıyor', shipped: 'Kargoda', in_transit: 'Sevkiyatta', completed: 'Tamamlandı', cancelled: 'İptal' };
  const PAYMENT_LABEL = { paid: 'Ödendi', pending: 'Bekliyor', on_account: 'Cari', failed: 'Başarısız' };
  const statusBadge = (status) => {
    if (status === 'completed' || status === 'paid') return 'bg-green-50 text-green-700';
    if (status === 'cancelled' || status === 'failed') return 'bg-red-50 text-red-700';
    if (status === 'pending') return 'bg-amber-50 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4" data-testid="account-movements">
      {/* Wolvox Link Banner */}
      {data.wolvox?.linked && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="admin-wolvox-banner">
          <Building2 className="w-4 h-4 text-indigo-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-indigo-900">Wolvox ERP'ye bağlı</p>
            <p className="text-[11px] text-indigo-700 truncate">
              CARI: <span className="font-mono">{data.wolvox.carikodu}</span> · {data.wolvox.unvani || '-'}
              {data.wolvox.last_sync && <span className="ml-2 text-indigo-500">Son sync: {new Date(data.wolvox.last_sync).toLocaleString('tr-TR')}</span>}
            </p>
          </div>
          {data.wolvox.bakiye !== null && data.wolvox.bakiye !== undefined && (
            <div className="text-right shrink-0">
              <p className="text-[10px] text-indigo-600 uppercase font-semibold">Wolvox Bakiye</p>
              <p className={`text-sm font-bold tabular-nums ${data.wolvox.bakiye > 0 ? 'text-[#1a224f]' : 'text-gray-900'}`} data-testid="admin-wolvox-bakiye">
                {formatCurrency(data.wolvox.bakiye)}
              </p>
            </div>
          )}
        </div>
      )}
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Borç (Siparişler)</p>
              <p className="text-lg font-bold text-gray-900 tabular-nums mt-0.5">{formatCurrency(data.summary.total_debit)}</p>
            </div>
            <div className="rounded-lg p-2 bg-gray-100"><ArrowUpRight className="w-4 h-4 text-red-500" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Alacak (Ödemeler)</p>
              <p className="text-lg font-bold text-gray-900 tabular-nums mt-0.5">{formatCurrency(data.summary.total_credit)}</p>
            </div>
            <div className="rounded-lg p-2 bg-gray-100"><ArrowDownRight className="w-4 h-4 text-green-500" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Bakiye</p>
              <p className={`text-lg font-bold tabular-nums mt-0.5 ${data.summary.balance > 0 ? 'text-[#1a224f]' : 'text-gray-900'}`}>{formatCurrency(data.summary.balance)}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{data.summary.balance > 0 ? 'Borçlu' : data.summary.balance < 0 ? 'Alacaklı' : 'Dengede'}</p>
            </div>
            <div className={`rounded-lg p-2 ${data.summary.balance > 0 ? 'bg-red-50' : 'bg-gray-100'}`}>
              <Wallet className={`w-4 h-4 ${data.summary.balance > 0 ? 'text-[#1a224f]' : 'text-gray-500'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Movements - 2026 Table Style */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Hesap Hareketleri</h2>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-gray-200 text-gray-600 rounded-md text-xs hover:bg-white" data-testid="admin-export-btn">
                <Download className="w-3.5 h-3.5" />Dışa Aktar
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => downloadExport('excel')} data-testid="admin-export-excel">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />Excel olarak indir
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadExport('pdf')} data-testid="admin-export-pdf">
                <FileDown className="w-4 h-4 mr-2 text-[#1a224f]" />PDF olarak indir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {data.movements.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <Wallet className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            <p className="text-sm">Henüz cari hareket yok</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="w-10 px-2 py-2 text-center text-xs font-semibold text-gray-500">#</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Tarih</th>
                    <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Açıklama</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-24">Kaynak</th>
                    <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500 w-28">Durum</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Borç</th>
                    <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-32">Alacak</th>
                  </tr>
                </thead>
                <tbody>
                  {data.movements.map((m, i) => {
                    const orderId = m.order_id || (m.source !== 'wolvox' ? m.id : null);
                    const clickable = !!orderId;
                    return (
                    <tr key={i} className={`border-b border-gray-100 last:border-0 ${i % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} ${clickable ? 'hover:bg-gray-100/50 cursor-pointer' : ''}`} onClick={() => clickable && openOrderDetail(orderId)} data-testid={`movement-row-${i}`}>
                      <td className="px-2 py-2.5 text-center text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-3 py-2.5 text-gray-500 text-xs whitespace-nowrap">{formatDate(m.date)}</td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          {m.type === 'debit' ? <ArrowUpRight className="w-3.5 h-3.5 text-red-500 shrink-0" /> : <ArrowDownRight className="w-3.5 h-3.5 text-green-500 shrink-0" />}
                          <div className="min-w-0">
                            <span className="font-medium text-gray-900 truncate block">{m.description}</span>
                            {m.evrak_no && <span className="text-[10px] text-gray-400 font-mono">{m.evrak_no}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        {m.source === 'wolvox' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700">Wolvox</span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-700">B2B</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${statusBadge(m.status)}`}>
                          {STATUS_LABEL[m.status] || PAYMENT_LABEL[m.status] || m.status}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right font-medium tabular-nums text-[#1a224f]">{m.type === 'debit' ? formatCurrency(m.amount) : ''}</td>
                      <td className="px-3 py-2.5 text-right font-medium tabular-nums text-green-600">{m.type === 'credit' ? formatCurrency(m.amount) : ''}</td>
                    </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                  <tr>
                    <td colSpan="5" className="px-3 py-2.5 font-semibold text-gray-900">Toplam</td>
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-[#1a224f]">{formatCurrency(data.summary.total_debit)}</td>
                    <td className="px-3 py-2.5 text-right font-bold tabular-nums text-green-600">{formatCurrency(data.summary.total_credit)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {data.movements.map((m, i) => {
                const orderId = m.order_id || (m.source !== 'wolvox' ? m.id : null);
                return (
                  <div key={i} className="flex items-center gap-3 px-3 py-3 hover:bg-gray-50" onClick={() => orderId && openOrderDetail(orderId)}>
                    {m.type === 'debit' ? <ArrowUpRight className="w-4 h-4 text-red-500 shrink-0" /> : <ArrowDownRight className="w-4 h-4 text-green-500 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{m.description}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-gray-400">{formatDate(m.date)}</span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium ${statusBadge(m.status)}`}>
                          {STATUS_LABEL[m.status] || PAYMENT_LABEL[m.status] || m.status}
                        </span>
                      </div>
                    </div>
                    <p className={`text-sm font-bold tabular-nums shrink-0 ${m.type === 'debit' ? 'text-[#1a224f]' : 'text-green-600'}`}>
                      {m.type === 'debit' ? '+' : '-'}{formatCurrency(m.amount)}
                    </p>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Order Detail Modal */}
      {(selectedOrder || orderLoading) && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/50" onClick={() => { setSelectedOrder(null); }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {orderLoading ? (
              <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /></div>
            ) : selectedOrder && (
              <>
                <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-bold text-gray-900">#{selectedOrder.order_number}</h2>
                    <p className="text-xs text-gray-500">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => downloadPdf(selectedOrder)} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1a224f] text-white rounded-lg text-xs font-medium hover:bg-[#12173a]">
                      <Download className="w-3.5 h-3.5" />PDF İndir
                    </button>
                    <button onClick={() => setSelectedOrder(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4 text-gray-500" /></button>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Sipariş Durumu</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(selectedOrder.status)}`}>
                        {STATUS_LABEL[selectedOrder.status] || selectedOrder.status}
                      </span>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Ödeme</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(selectedOrder.payment_status)}`}>
                        {PAYMENT_LABEL[selectedOrder.payment_status] || selectedOrder.payment_status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold mb-2">Sipariş İçeriği ({selectedOrder.item_count})</p>
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full text-[12px]">
                        <thead><tr className="bg-gray-50/50 border-b border-gray-100">
                          <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Ürün</th>
                          <th className="text-center px-2 py-2 text-xs font-semibold text-gray-500 w-14">Adet</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-24">Birim</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-gray-500 w-24">Toplam</th>
                        </tr></thead>
                        <tbody>
                          {(selectedOrder.items || []).map((item, idx) => (
                            <tr key={idx} className={`border-b border-gray-100 last:border-0 ${idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'}`}>
                              <td className="px-3 py-2"><p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                                <p className="text-[10px] text-gray-400">
                                  {item.iskonto_orani > 0 && <span className="text-[#1a224f] font-medium">%{item.iskonto_orani} isk.</span>}
                                  {item.kdv_orani > 0 && <span className="ml-1">+%{item.kdv_orani} KDV</span>}
                                </p>
                              </td>
                              <td className="px-2 py-2 text-center tabular-nums">{item.quantity}</td>
                              <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(item.unit_price)}</td>
                              <td className="px-3 py-2 text-right font-semibold tabular-nums">{formatCurrency(item.total_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 space-y-1">
                    {(() => {
                      const net = (selectedOrder.items || []).reduce((s, i) => s + (i.unit_price * i.quantity), 0);
                      const kdv = (selectedOrder.items || []).reduce((s, i) => s + (i.unit_price * i.quantity * (i.kdv_orani || 0) / 100), 0);
                      return (
                        <>
                          <div className="flex justify-between text-xs"><span className="text-gray-500">Net</span><span className="font-medium tabular-nums">{formatCurrency(net)}</span></div>
                          {kdv > 0 && <div className="flex justify-between text-xs"><span className="text-gray-500">KDV</span><span className="font-medium tabular-nums">{formatCurrency(kdv)}</span></div>}
                          <div className="flex justify-between text-sm font-bold pt-1.5 border-t border-gray-200"><span>Toplam</span><span className="text-[#1a224f] tabular-nums">{formatCurrency(net + kdv)}</span></div>
                        </>
                      );
                    })()}
                  </div>
                  {selectedOrder.shipping_address && (
                    <div><p className="text-[10px] text-gray-400 uppercase font-semibold mb-1.5">Teslimat Adresi</p><p className="text-sm text-gray-700">{selectedOrder.shipping_address}</p></div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDealersPage() {
  const { token } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dealers, setDealers] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDealer, setEditingDealer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Detail view
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [detailTab, setDetailTab] = useState('account');
  const [resetPasswordForm, setResetPasswordForm] = useState('');
  const [resettingPassword, setResettingPassword] = useState(false);
  const [dealerUsers, setDealerUsers] = useState([]);

  const [form, setForm] = useState({
    email: '', password: '', full_name: '', phone: '',
    company_name: '', address: '', city: '', district: '', tax_number: '', assigned_personnel_id: ''
  });

  const [sortBy, setSortBy] = useState('debt_desc');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedRows, setSelectedRows] = useState([]);

  // Import/Export/Bulk Actions
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showBulkGroupModal, setShowBulkGroupModal] = useState(false);
  const [showBulkPersonnelModal, setShowBulkPersonnelModal] = useState(false);
  const [bulkGroupValue, setBulkGroupValue] = useState('');
  const [bulkPersonnelId, setBulkPersonnelId] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const importFileRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const exportMenuRef = useRef(null);

  // Regions (Bolgeler)
  const [regions, setRegions] = useState([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | passive
  const [filterRegion, setFilterRegion] = useState('');
  const [filterPersonnel, setFilterPersonnel] = useState('');
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showRegionManager, setShowRegionManager] = useState(false);
  const filterPanelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // Ignore clicks inside any Radix Portal (Select dropdown, Dialog, etc.)
      const inRadixPortal = e.target.closest && (
        e.target.closest('[data-radix-popper-content-wrapper]') ||
        e.target.closest('[role="listbox"]') ||
        e.target.closest('[role="dialog"]')
      );
      if (inRadixPortal) return;
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setShowBulkMenu(false);
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setShowExportMenu(false);
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) setShowFilterPanel(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const sortedDealers = React.useMemo(() => {
    let list = [...dealers];
    if (filterStatus === 'active') list = list.filter(d => d.is_active);
    else if (filterStatus === 'passive') list = list.filter(d => !d.is_active);
    switch (sortBy) {
      case 'debt_desc': return list.sort((a, b) => (b.total_debt || 0) - (a.total_debt || 0));
      case 'debt_asc': return list.sort((a, b) => (a.total_debt || 0) - (b.total_debt || 0));
      case 'name_asc': return list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr'));
      case 'name_desc': return list.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'tr'));
      case 'newest': return list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      default: return list;
    }
  }, [dealers, sortBy, filterStatus]);

  useEffect(() => {
    fetchDealers();
    fetchPersonnel();
    fetchRegions();
  }, [search, filterRegion, filterPersonnel]);

  // Auto-open dealer detail if ?dealer_id=xxx is in URL
  useEffect(() => {
    const targetId = searchParams.get('dealer_id');
    if (targetId && dealers.length > 0 && (!selectedDealer || selectedDealer.id !== targetId)) {
      const found = dealers.find(d => d.id === targetId);
      if (found) {
        setSelectedDealer(found);
        setDetailTab('account');
      }
    }
  }, [searchParams, dealers]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDealers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      // Status filter applied client-side to keep stat pills stable
      if (filterRegion) params.append('region', filterRegion);
      if (filterPersonnel) params.append('personnel_id', filterPersonnel);
      params.append('limit', '1000');
      const res = await axios.get(`${API_URL}/api/dealers?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDealers(res.data.dealers || []);
    } catch (err) {
      toast.error('Bayiler yuklenemedi');
    } finally { setLoading(false); }
  };

  const fetchRegions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dealer-regions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegions(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const createRegion = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    try {
      const res = await axios.post(`${API_URL}/api/dealer-regions`,
        { name: trimmed },
        { headers: { Authorization: `Bearer ${token}` } });
      await fetchRegions();
      return res.data;
    } catch {
      toast.error('Bolge olusturulamadi');
      return null;
    }
  };

  const deleteRegion = async (id) => {
    if (!window.confirm('Bu bolgeyi silmek istiyor musunuz? Bayilerden temizlenecek.')) return;
    try {
      await axios.delete(`${API_URL}/api/dealer-regions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bolge silindi');
      fetchRegions();
      fetchDealers();
    } catch { toast.error('Silinemedi'); }
  };

  const renameRegion = async (region) => {
    const newName = window.prompt('Yeni bolge adi:', region.name);
    if (!newName || !newName.trim() || newName.trim() === region.name) return;
    try {
      await axios.put(`${API_URL}/api/dealer-regions/${region.id}`,
        { name: newName.trim() },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bolge yeniden adlandirildi');
      fetchRegions();
      fetchDealers();
      // If currently filtered by old name, update the filter
      if (filterRegion === region.name) setFilterRegion(newName.trim());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yeniden adlandirilamadi');
    }
  };

  const fetchPersonnel = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/personnel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPersonnel(Array.isArray(res.data) ? res.data : res.data.users || []);
    } catch {}
  };

  const openAddModal = () => {
    setEditingDealer(null);
    setForm({ email: '', password: '', full_name: '', phone: '', company_name: '', address: '', city: '', district: '', tax_number: '', assigned_personnel_id: '', iskonto_orani: '', group_tag: '' });
    setShowModal(true);
  };

  const openEditModal = (dealer) => {
    setEditingDealer(dealer);
    setForm({
      email: dealer.email || '', password: '',
      full_name: dealer.name || '', phone: dealer.phone || '',
      company_name: dealer.company_name || '', address: dealer.address || '',
      city: dealer.city || '', district: dealer.district || '',
      tax_number: dealer.tax_number || '', assigned_personnel_id: dealer.assigned_personnel_id || '',
      iskonto_orani: dealer.iskonto_orani || '', group_tag: dealer.group_tag || ''
    });
    setShowModal(true);
  };

  const openDetail = (dealer) => {
    setSelectedDealer(dealer);
    setDetailTab('account');
    setResetPasswordForm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.company_name) { toast.error('Email ve Firma Adi zorunludur'); return; }
    if (!editingDealer && !form.password) { toast.error('Sifre zorunludur'); return; }
    setSaving(true);
    try {
      if (editingDealer) {
        const payload = { full_name: form.full_name || form.company_name, phone: form.phone || null, company_name: form.company_name || null, address: form.address || null, city: form.city || null, district: form.district || null, tax_number: form.tax_number || null, assigned_personnel_id: form.assigned_personnel_id || null, iskonto_orani: form.iskonto_orani ? parseFloat(form.iskonto_orani) : 0, group_tag: form.group_tag || null };
        await axios.put(`${API_URL}/api/dealers/${editingDealer.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Bayi güncellendi');
      } else {
        await axios.post(`${API_URL}/api/dealers`, { ...form, full_name: form.full_name || null, iskonto_orani: form.iskonto_orani ? parseFloat(form.iskonto_orani) : 0 }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Bayi olusturuldu');
      }
      setShowModal(false);
      fetchDealers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bayi kaydedilemedi');
    } finally { setSaving(false); }
  };

  const handleDelete = async (dealer) => {
    if (!window.confirm(`"${dealer.name}" bayisini silmek istediginize emin misiniz?`)) return;
    setDeleting(dealer.id);
    try {
      await axios.delete(`${API_URL}/api/dealers/${dealer.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Bayi silindi');
      if (selectedDealer?.id === dealer.id) setSelectedDealer(null);
      fetchDealers();
    } catch { toast.error('Bayi silinemedi'); }
    finally { setDeleting(null); }
  };

  const toggleActive = async (dealer) => {
    try {
      await axios.put(`${API_URL}/api/dealers/${dealer.id}`, { is_active: !dealer.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(dealer.is_active ? 'Bayi deaktif edildi' : 'Bayi aktif edildi');
      fetchDealers();
      if (selectedDealer?.id === dealer.id) {
        setSelectedDealer({ ...selectedDealer, is_active: !dealer.is_active });
      }
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordForm || resetPasswordForm.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır');
      return;
    }
    setResettingPassword(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${selectedDealer.id}/reset-password`, { new_password: resetPasswordForm }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Şifre başarıyla sıfırlandı');
      setResetPasswordForm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Şifre sıfırlanamadı');
    } finally { setResettingPassword(false); }
  };

  const getPersonnelName = (id) => {
    const p = personnel.find(p => p.id === id);
    return p ? p.name : '-';
  };

  // ---------- Import/Export/Bulk Handlers ----------
  const handleDownloadTemplate = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dealers/import-template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'bayi_import_sablonu.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sablon indirildi');
    } catch { toast.error('Sablon indirilemedi'); }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Lutfen bir dosya secin'); return; }
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post(`${API_URL}/api/dealers/import`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImportResult(res.data);
      if (res.data.imported > 0) {
        toast.success(`${res.data.imported} bayi basariyla ice aktarildi`);
        fetchDealers();
      }
      if (res.data.skipped_count > 0) {
        toast.warning(`${res.data.skipped_count} bayi atlandi (duplike)`);
      }
      // Auto-close if everything imported cleanly
      if (res.data.imported > 0 && res.data.skipped_count === 0 && res.data.error_count === 0) {
        setTimeout(() => {
          setShowImportModal(false);
          setImportFile(null);
          setImportResult(null);
          if (importFileRef.current) importFileRef.current.value = '';
        }, 1200);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ice aktarma basarisiz');
    } finally { setImporting(false); }
  };

  const handleExport = async (fmt) => {
    setShowExportMenu(false);
    try {
      const res = await axios.get(`${API_URL}/api/dealers/export?format=${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const ext = fmt === 'csv' ? 'csv' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bayiler.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dosya indirildi');
    } catch { toast.error('Disa aktarma basarisiz'); }
  };

  const runBulkAction = async (action, value) => {
    if (selectedRows.length === 0) { toast.error('Once bayi secin'); return; }
    setBulkProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/dealers/bulk-action`, {
        ids: selectedRows, action, value: value ?? null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${res.data.affected} bayi guncellendi`);
      setSelectedRows([]);
      setShowBulkMenu(false);
      setShowBulkGroupModal(false);
      setShowBulkPersonnelModal(false);
      setBulkGroupValue('');
      setBulkPersonnelId('');
      fetchDealers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toplu islem basarisiz');
    } finally { setBulkProcessing(false); }
  };

  const handleBulkDelete = () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`${selectedRows.length} bayiyi kalici olarak silmek istediginize emin misiniz? Bu islem geri alinamaz.`)) return;
    runBulkAction('delete');
  };

  // Detail View
  if (selectedDealer) {
    return (
      <div className="space-y-3 sm:space-y-6" data-testid="dealer-detail-view">
        {/* Back Button */}
        <button onClick={() => { setSelectedDealer(null); if (searchParams.get('dealer_id')) { searchParams.delete('dealer_id'); setSearchParams(searchParams); } }} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900" data-testid="back-to-dealers">
          <ArrowLeft className="w-4 h-4" />Bayilere Dön
        </button>

        {/* Dealer Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center shrink-0 ${selectedDealer.is_active ? 'bg-red-100' : 'bg-gray-100'}`}>
                <span className={`text-lg sm:text-2xl font-bold ${selectedDealer.is_active ? 'text-[#1a224f]' : 'text-gray-400'}`}>
                  {(selectedDealer.company_name || selectedDealer.name || 'B').charAt(0)}
                </span>
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 truncate">{selectedDealer.company_name || selectedDealer.name}</h1>
                {selectedDealer.company_name && selectedDealer.name && selectedDealer.name !== selectedDealer.company_name && (
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{selectedDealer.name}</p>
                )}
                <p className="text-xs sm:text-sm text-gray-400 truncate">{selectedDealer.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto sm:ml-0">
              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${selectedDealer.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {selectedDealer.is_active ? 'Aktif' : 'Pasif'}
              </span>
              <button onClick={() => openEditModal(selectedDealer)} className="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-1">
                <Edit2 className="w-4 h-4" />Duzenle
              </button>
              <button onClick={() => toggleActive(selectedDealer)} className={`p-2 rounded-lg text-sm ${selectedDealer.is_active ? 'text-yellow-700 hover:bg-yellow-50' : 'text-green-700 hover:bg-green-50'}`}>
                {selectedDealer.is_active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[
            { id: 'account', label: 'Cari Hareketleri', icon: Building2 },
            { id: 'info', label: 'Bilgiler', icon: Building2 },
            { id: 'users', label: 'Kullanici Yonetimi', icon: Users },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setDetailTab(tab.id)}
              className={`flex items-center gap-1.5 px-2 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-all flex-1 justify-center ${
                detailTab === tab.id ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`detail-tab-${tab.id}`}
            >
              <tab.icon className="h-4 w-4 hidden sm:block" />{tab.label}
            </button>
          ))}
        </div>

        {/* Account Movements Tab */}
        {detailTab === 'account' && <DealerAccountMovements dealerId={selectedDealer.id} token={token} />}

        {/* Info Tab */}
        {detailTab === 'info' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-4">
            <h3 className="font-semibold text-gray-900">Bayi Bilgileri</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { icon: Mail, label: 'Email', value: selectedDealer.email },
                { icon: Phone, label: 'Telefon', value: selectedDealer.phone || '-' },
                { icon: Building2, label: 'Firma', value: selectedDealer.company_name || '-' },
                { icon: MapPin, label: 'Adres', value: selectedDealer.address || '-' },
                { icon: MapPin, label: 'Il', value: selectedDealer.city || '-' },
                { icon: Shield, label: 'Vergi No', value: selectedDealer.tax_number || '-' },
                { icon: UserCheck, label: 'Sorumlu Personel', value: selectedDealer.assigned_personnel_id ? getPersonnelName(selectedDealer.assigned_personnel_id) : '-' },
                { icon: Percent, label: 'İskonto Oranı', value: selectedDealer.iskonto_orani ? `%${selectedDealer.iskonto_orani}` : 'İskonto yok' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                  <item.icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="text-sm font-medium text-gray-900">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Users Tab */}
        {detailTab === 'users' && (
          <div className="space-y-4">
            {/* Current User Info */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-4">
              <h3 className="font-semibold text-gray-900">Mevcut Kullanici</h3>
              <div className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg bg-gray-50">
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                  <span className="text-lg font-bold text-[#1a224f]">{selectedDealer.name?.charAt(0) || 'B'}</span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{selectedDealer.name}</p>
                  <p className="text-sm text-gray-500">{selectedDealer.email}</p>
                  <p className="text-xs text-gray-400">Rol: Bayi</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${selectedDealer.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {selectedDealer.is_active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
            </div>

            {/* Password Reset */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-red-500" />
                <h3 className="font-semibold text-gray-900">Sifre Sifirlama</h3>
              </div>
              <p className="text-xs sm:text-sm text-gray-500">Bu bayi icin yeni bir sifre belirleyin. Degisiklik hemen gecerli olacaktir.</p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <input
                  type="password"
                  value={resetPasswordForm}
                  onChange={(e) => setResetPasswordForm(e.target.value)}
                  placeholder="Yeni sifre (min. 8 karakter)"
                  className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  data-testid="reset-password-input"
                />
                <button
                  onClick={handleResetPassword}
                  disabled={resettingPassword || !resetPasswordForm}
                  className="px-4 py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                  data-testid="reset-password-btn"
                >
                  {resettingPassword ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  Sifreyi Sifirla
                </button>
              </div>
            </div>

            {/* Activate/Deactivate */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-3 sm:space-y-4">
              <h3 className="font-semibold text-gray-900">Hesap Durumu</h3>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 sm:p-4 rounded-lg bg-gray-50">
                <div>
                  <p className="text-sm font-medium text-gray-900">Bayi Hesabi {selectedDealer.is_active ? 'Aktif' : 'Pasif'}</p>
                  <p className="text-xs text-gray-500">{selectedDealer.is_active ? 'Kullanici giris yapabilir' : 'Kullanici giris yapamaz'}</p>
                </div>
                <button
                  onClick={() => toggleActive(selectedDealer)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium w-full sm:w-auto text-center ${
                    selectedDealer.is_active ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                  data-testid="toggle-active-btn"
                >
                  {selectedDealer.is_active ? 'Deaktif Et' : 'Aktif Et'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const activeCount = dealers.filter(d => d.is_active).length;
  const passiveCount = dealers.filter(d => !d.is_active).length;

  const totalPages = Math.ceil(sortedDealers.length / pageSize);
  const paginated = sortedDealers.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const toggleRow = (id) => setSelectedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleAllRows = () => { if (selectedRows.length === paginated.length) setSelectedRows([]); else setSelectedRows(paginated.map(d => d.id)); };

  return (
    <div className="space-y-4" data-testid="admin-dealers-page">
      {/* Stats Pills (clickable filter) + Primary Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        {/* Stat Pills */}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setFilterStatus('all'); setCurrentPage(0); }} className={`px-3 py-2 border rounded-lg text-xs sm:text-sm transition-colors ${filterStatus === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200 hover:bg-gray-50'}`} data-testid="stat-filter-all">
            <span className="font-bold">{dealers.length}</span> <span className={filterStatus === 'all' ? 'text-gray-200' : 'text-gray-500'}>Toplam</span>
          </button>
          <button onClick={() => { setFilterStatus('active'); setCurrentPage(0); }} className={`px-3 py-2 border rounded-lg text-xs sm:text-sm transition-colors ${filterStatus === 'active' ? 'bg-green-600 text-white border-green-600' : 'bg-white border-gray-200 hover:bg-green-50'}`} data-testid="stat-filter-active">
            <span className="font-bold">{activeCount}</span> <span className={filterStatus === 'active' ? 'text-green-100' : 'text-gray-500'}>Aktif</span>
          </button>
          <button onClick={() => { setFilterStatus('passive'); setCurrentPage(0); }} className={`px-3 py-2 border rounded-lg text-xs sm:text-sm transition-colors ${filterStatus === 'passive' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white border-gray-200 hover:bg-amber-50'}`} data-testid="stat-filter-passive">
            <span className="font-bold">{passiveCount}</span> <span className={filterStatus === 'passive' ? 'text-amber-100' : 'text-gray-500'}>Pasif</span>
          </button>
        </div>

        {/* Primary Action Buttons - mobilde 3 eşit grid, desktop'ta yan yana */}
        <div className="grid grid-cols-3 sm:flex sm:items-center gap-2 sm:ml-auto">
          <button onClick={openAddModal} className="inline-flex items-center justify-center gap-1 px-2 sm:px-4 py-2 bg-gray-900 text-white rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-800 transition-colors" data-testid="add-dealer-btn">
            <Plus className="w-4 h-4 shrink-0" />
            <span className="hidden xs:inline sm:inline">Yeni Bayi</span>
            <span className="xs:hidden sm:hidden">Yeni</span>
          </button>
          <button onClick={() => { setImportFile(null); setImportResult(null); setShowImportModal(true); }} className="inline-flex items-center justify-center gap-1 px-2 sm:px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors" data-testid="import-dealers-btn">
            <Upload className="w-4 h-4 shrink-0" />
            <span>İçe Aktar</span>
          </button>
          <div className="relative" ref={exportMenuRef}>
            <button onClick={() => setShowExportMenu(!showExportMenu)} className="w-full inline-flex items-center justify-center gap-1 px-2 sm:px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors" data-testid="export-dealers-btn">
              <Download className="w-4 h-4 shrink-0" /><span>Dışa Aktar</span><ChevronDown className="w-3 h-3 hidden sm:inline" />
            </button>
            {showExportMenu && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                <button onClick={() => handleExport('xlsx')} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="export-xlsx-btn"><FileText className="w-4 h-4 text-green-600" />Excel (.xlsx)</button>
                <button onClick={() => handleExport('csv')} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="export-csv-btn"><FileText className="w-4 h-4 text-gray-600" />CSV (.csv)</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active Filter Chips - removed per user request */}

      {/* Action Bar */}
      <div className="flex flex-col gap-3">
        {/* Top row: Bulk actions / hint (mobilde tek başına) */}
        <div className="flex items-center gap-2 flex-wrap">
          {selectedRows.length > 0 ? (
            <>
              <span className="text-xs text-gray-600 bg-gray-100 px-3 py-2 rounded-lg font-medium whitespace-nowrap">{selectedRows.length} bayi seçili</span>
              <div className="relative" ref={bulkMenuRef}>
                <button onClick={() => setShowBulkMenu(!showBulkMenu)} disabled={bulkProcessing} className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-60" data-testid="bulk-actions-btn">
                  {bulkProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
                  Toplu İşlemler<ChevronDown className="w-3 h-3" />
                </button>
                {showBulkMenu && (
                  <div className="absolute left-0 top-full mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20 overflow-hidden">
                    <button onClick={() => { setShowBulkMenu(false); setShowBulkGroupModal(true); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="bulk-group-btn"><Tag className="w-4 h-4 text-gray-500" />Bölgeye Ata</button>
                    <button onClick={() => { setShowBulkMenu(false); setShowBulkPersonnelModal(true); }} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="bulk-personnel-btn"><UserCheck className="w-4 h-4 text-gray-500" />Personel Ata</button>
                    <button onClick={() => runBulkAction('activate')} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="bulk-activate-btn"><Power className="w-4 h-4 text-green-600" />Aktif Yap</button>
                    <button onClick={() => runBulkAction('deactivate')} className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="bulk-deactivate-btn"><Power className="w-4 h-4 text-amber-600" />Pasif Yap</button>
                    <div className="border-t border-gray-100" />
                    <button onClick={handleBulkDelete} className="w-full px-3 py-2 text-left text-sm hover:bg-[#eef0f7] text-[#1a224f] flex items-center gap-2" data-testid="bulk-delete-btn"><Trash2 className="w-4 h-4" />Seçilenleri Sil</button>
                  </div>
                )}
              </div>
              <button onClick={() => setSelectedRows([])} className="text-xs text-gray-500 hover:text-gray-800 whitespace-nowrap">Seçimi temizle</button>
            </>
          ) : (
            <span className="text-xs text-gray-400 hidden sm:inline">Tümünü seçmek için tablo başlığından kutuyu kullanın</span>
          )}
        </div>

        {/* Bottom row: filters - mobile-first responsive */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          {/* Search - mobilde full width, desktop'ta sabit genişlik */}
          <div className="relative flex-1 sm:flex-initial sm:w-56 order-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Bayi ara..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(0); }}
              className="w-full pl-8 pr-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400"
              data-testid="search-input"
            />
          </div>

          {/* Sort + PageSize + Filter - mobilde 3'ü yan yana eşit grid, desktop'ta yan yana */}
          <div className="grid grid-cols-3 sm:flex sm:items-center gap-2 order-2">
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(0); }}>
              <SelectTrigger className="h-9 px-2 border-gray-200 rounded-lg text-sm bg-white sm:w-[72px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={(val) => { setSortBy(val); setCurrentPage(0); }}>
              <SelectTrigger className="h-9 px-2 border-gray-200 rounded-lg text-sm bg-white sm:min-w-[140px]" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="debt_desc">Borç (Yüksek)</SelectItem>
                <SelectItem value="debt_asc">Borç (Düşük)</SelectItem>
                <SelectItem value="name_asc">İsim (A-Z)</SelectItem>
                <SelectItem value="name_desc">İsim (Z-A)</SelectItem>
                <SelectItem value="newest">En Yeni</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative" ref={filterPanelRef}>
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${(filterRegion || filterPersonnel || filterStatus !== 'all') ? 'bg-[#1a224f] text-white border-[#1a224f] hover:bg-[#12173a]' : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                data-testid="filter-btn"
              >
                <Filter className="w-4 h-4" />
                <span>Filtrele</span>
                {(filterRegion || filterPersonnel || filterStatus !== 'all') && (
                  <span className="ml-0.5 px-1.5 py-0.5 bg-white/20 text-[10px] font-bold rounded">
                    {[filterStatus !== 'all', !!filterRegion, !!filterPersonnel].filter(Boolean).length}
                  </span>
                )}
              </button>
            {showFilterPanel && (
              <>
                {/* Mobile: bottom sheet overlay */}
                <div className="sm:hidden fixed inset-0 z-30 bg-black/40" onClick={() => setShowFilterPanel(false)} />
                <div
                  className="fixed left-0 right-0 bottom-0 sm:absolute sm:left-auto sm:right-0 sm:bottom-auto sm:top-full
                             w-full sm:w-72 bg-white border-t sm:border border-gray-200
                             rounded-t-2xl sm:rounded-xl shadow-xl z-40
                             p-4 space-y-3 max-h-[85vh] overflow-y-auto sm:mt-2"
                  data-testid="filter-panel"
                >
                  {/* Mobile pull handle */}
                  <div className="sm:hidden -mt-2 mb-1 flex justify-center"><div className="w-10 h-1 bg-gray-300 rounded-full" /></div>
                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-semibold text-base sm:text-sm text-gray-900">Filtreler</h4>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { setShowFilterPanel(false); setShowRegionManager(true); }} className="text-xs text-gray-500 hover:text-gray-900 underline" data-testid="filter-manage-regions">Bolgeler</button>
                      <button onClick={() => { setFilterStatus('all'); setFilterRegion(''); setFilterPersonnel(''); }} className="text-xs text-gray-500 hover:text-gray-900 underline">Sifirla</button>
                      <button onClick={() => setShowFilterPanel(false)} className="sm:hidden p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4 text-gray-500" /></button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Durum</label>
                    <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v)}>
                      <SelectTrigger className="w-full h-10 sm:h-9 border-gray-200 rounded-lg text-sm" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Hepsi</SelectItem>
                        <SelectItem value="active">Aktif</SelectItem>
                        <SelectItem value="passive">Pasif</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Bolge</label>
                    <Select value={filterRegion || '_none_'} onValueChange={(v) => setFilterRegion(v === '_none_' ? '' : v)}>
                      <SelectTrigger className="w-full h-10 sm:h-9 border-gray-200 rounded-lg text-sm" data-testid="filter-region-select"><SelectValue placeholder="Tum bolgeler" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="_none_">Tum bolgeler</SelectItem>
                        {regions.map(r => (
                          <SelectItem key={r.id} value={r.name}>{r.name}{r.dealer_count != null && ` (${r.dealer_count})`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Satis Personeli</label>
                    <Select value={filterPersonnel || '_none_'} onValueChange={(v) => setFilterPersonnel(v === '_none_' ? '' : v)}>
                      <SelectTrigger className="w-full h-10 sm:h-9 border-gray-200 rounded-lg text-sm" data-testid="filter-personnel-select"><SelectValue placeholder="Tum personel" /></SelectTrigger>
                      <SelectContent className="max-h-64">
                        <SelectItem value="_none_">Tum personel</SelectItem>
                        {personnel.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <button onClick={() => setShowFilterPanel(false)} className="w-full mt-2 px-3 py-2.5 sm:py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800" data-testid="filter-apply-btn">
                    Uygula
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gray-300" /></div>
      ) : sortedDealers.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-lg p-16 text-center">
          <Users className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-500">{search ? 'Sonuc bulunamadı' : 'Henuz bayi yok'}</p>
        </div>
      ) : (
        <>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="w-10 px-3 py-2.5"><input type="checkbox" checked={selectedRows.length === paginated.length && paginated.length > 0} onChange={toggleAllRows} className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer" data-testid="select-all-checkbox" /></th>
                  <th className="w-10 px-2 py-2.5 text-left text-xs font-semibold text-gray-500">#</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500">Firma</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 hidden md:table-cell">Telefon</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 hidden lg:table-cell">Personel</th>
                  <th className="text-left px-3 py-2.5 text-xs font-semibold text-gray-500 hidden lg:table-cell">Bolge</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 w-16">Aktif</th>
                  <th className="text-center px-3 py-2.5 text-xs font-semibold text-gray-500 hidden sm:table-cell w-16">Isk%</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-500">Borc</th>
                  <th className="text-right px-3 py-2.5 text-xs font-semibold text-gray-500 hidden xl:table-cell">Olusturulma</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((dealer, idx) => {
                  const rowNum = currentPage * pageSize + idx + 1;
                  const isSelected = selectedRows.includes(dealer.id);
                  return (
                    <tr key={dealer.id} className={`border-b border-gray-100 group transition-colors ${isSelected ? 'bg-blue-50/40' : idx % 2 === 1 ? 'bg-gray-50/30' : 'bg-white'} hover:bg-gray-100/50`} data-testid={`dealer-row-${dealer.id}`}>
                      <td className="px-3 py-2"><input type="checkbox" checked={isSelected} onChange={() => toggleRow(dealer.id)} className="w-3.5 h-3.5 rounded border-gray-300 cursor-pointer" data-testid={`row-checkbox-${dealer.id}`} /></td>
                      <td className="px-2 py-2 text-gray-400 text-xs">{rowNum}</td>
                      <td className="px-3 py-2">
                        <div>
                          <p className="font-medium text-gray-900">{dealer.company_name || dealer.name}</p>
                          {dealer.company_name && dealer.name && dealer.name !== dealer.company_name && (
                            <p className="text-[11px] text-gray-400">{dealer.name}</p>
                          )}
                          <div className="flex items-center gap-2 mt-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => openDetail(dealer)} className="text-[11px] text-gray-500 hover:text-gray-800 font-medium" data-testid={`show-${dealer.id}`}>Goster</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={() => openEditModal(dealer)} className="text-[11px] text-gray-500 hover:text-gray-800 font-medium" data-testid={`edit-${dealer.id}`}>Duzenle</button>
                            <span className="text-gray-300">|</span>
                            <button onClick={() => handleDelete(dealer)} className="text-[11px] text-red-400 hover:text-[#1a224f] font-medium" data-testid={`delete-${dealer.id}`}>Sil</button>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 hidden md:table-cell">
                        <p className="text-gray-600">{dealer.phone || '-'}</p>
                        <p className="text-[11px] text-gray-400 truncate max-w-[180px]">{dealer.email}</p>
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        <span className="text-xs text-gray-600">{dealer.assigned_personnel_name || '-'}</span>
                      </td>
                      <td className="px-3 py-2 hidden lg:table-cell">
                        {dealer.group_tag ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setFilterRegion(dealer.group_tag); setCurrentPage(0); }}
                            className="inline-block px-2 py-0.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-[11px] font-medium rounded transition-colors"
                            title={`"${dealer.group_tag}" ile filtrele`}
                            data-testid={`region-chip-${dealer.id}`}
                          >
                            {dealer.group_tag}
                          </button>
                        ) : <span className="text-xs text-gray-400">-</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={(e) => { e.stopPropagation(); toggleActive(dealer); }} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${dealer.is_active ? 'bg-gray-900' : 'bg-gray-300'}`} data-testid={`toggle-${dealer.id}`}>
                          <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${dealer.is_active ? 'translate-x-4' : 'translate-x-1'}`} />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-center hidden sm:table-cell">
                        <span className="text-gray-700 text-xs font-medium">{dealer.iskonto_orani > 0 ? `%${dealer.iskonto_orani}` : '-'}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={`font-semibold text-xs ${(dealer.total_debt || 0) > 0 ? 'text-[#1a224f]' : 'text-gray-800'}`}>{formatCurrency(dealer.total_debt || 0)}</span>
                      </td>
                      <td className="px-3 py-2 text-right hidden xl:table-cell">
                        <span className="text-xs text-gray-400">{dealer.created_at ? new Date(dealer.created_at).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">{currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, sortedDealers.length)} / {sortedDealers.length}</p>
              <div className="flex items-center gap-1">
                <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0} className="px-2 py-1.5 text-xs border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-30">{'<<'}</button>
                <button onClick={() => setCurrentPage(Math.max(0, currentPage - 1))} disabled={currentPage === 0} className="px-2.5 py-1.5 text-xs border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-30">{'<'}</button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i;
                  else if (currentPage < 3) p = i;
                  else if (currentPage > totalPages - 4) p = totalPages - 5 + i;
                  else p = currentPage - 2 + i;
                  return (
                    <button key={p} onClick={() => setCurrentPage(p)} className={`w-7 h-7 text-xs rounded transition-colors ${p === currentPage ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-600'}`}>{p + 1}</button>
                  );
                })}
                <button onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))} disabled={currentPage === totalPages - 1} className="px-2.5 py-1.5 text-xs border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-30">{'>'}</button>
                <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage === totalPages - 1} className="px-2 py-1.5 text-xs border border-gray-200 rounded bg-white hover:bg-gray-50 disabled:opacity-30">{'>>'}</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal - Mobilde tam ekran, desktop'ta centered */}
      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-stretch sm:items-center justify-center sm:p-4 bg-black/50">
          <div className="bg-white w-full sm:max-w-lg sm:w-full sm:rounded-2xl shadow-xl flex flex-col h-full sm:h-auto sm:max-h-[90vh]">
            {/* Sticky Modal Header */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3 sm:py-4 flex items-center justify-between shrink-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900">
                {editingDealer ? 'Bayi Düzenle' : 'Yeni Bayi Ekle'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 -mr-2 hover:bg-gray-100 rounded-lg active:bg-gray-200 transition-colors"
                aria-label="Kapat"
                data-testid="close-dealer-modal"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Scrollable Form Body */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4" id="dealer-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="bayi@example.com"
                  disabled={!!editingDealer}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680] disabled:bg-gray-50"
                  required
                  data-testid="email-input"
                />
              </div>

              {!editingDealer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Şifre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                    required
                    data-testid="password-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">En az 8 karakter, büyük harf, küçük harf ve rakam içermeli</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firma Adı <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                  placeholder="Firma Adı"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  required
                  data-testid="company-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Yetkili kisi (opsiyonel)"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  data-testid="fullname-input"
                />
                <p className="text-xs text-gray-500 mt-1">Firmadaki yetkili kisi (bos birakilirsa firma adi kullanilir)</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="05XX XXX XXXX"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Vergi No</label>
                  <input
                    type="text"
                    value={form.tax_number}
                    onChange={(e) => setForm({ ...form, tax_number: e.target.value })}
                    placeholder="Vergi Numarası"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Adres</label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Açık adres"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İl</label>
                  <input
                    type="text"
                    value={form.city || ''}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    placeholder="Istanbul"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                    data-testid="city-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">İlçe</label>
                  <input
                    type="text"
                    value={form.district || ''}
                    onChange={(e) => setForm({ ...form, district: e.target.value })}
                    placeholder="Kadikoy"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                    data-testid="district-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">İskonto Oranı (%)</label>
                <input
                  type="number"
                  value={form.iskonto_orani}
                  onChange={(e) => setForm({ ...form, iskonto_orani: e.target.value })}
                  placeholder="0"
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#3a4680]"
                  data-testid="dealer-iskonto-input"
                />
                <p className="text-xs text-gray-500 mt-1">Bu bayiye uygulanacak genel iskonto oranı</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">Bolge</label>
                  <button type="button" onClick={() => setShowRegionManager(true)} className="text-[11px] text-gray-500 hover:text-gray-900 underline" data-testid="manage-regions-link">Bolgeleri yonet</button>
                </div>
                <Select
                  value={form.group_tag || '_none_'}
                  onValueChange={async (val) => {
                    if (val === '__add_new__') {
                      const newName = window.prompt('Yeni bolge adi:');
                      if (newName && newName.trim()) {
                        const created = await createRegion(newName);
                        if (created) {
                          setForm({ ...form, group_tag: created.name });
                          toast.success(`"${created.name}" bolgesi olusturuldu`);
                        }
                      }
                      return;
                    }
                    setForm({ ...form, group_tag: val === '_none_' ? '' : val });
                  }}
                >
                  <SelectTrigger className="w-full h-10 border-gray-200 rounded-lg text-sm" data-testid="dealer-region-select">
                    <SelectValue placeholder="Bolge seciniz" />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    <SelectItem value="_none_">Bolgesiz</SelectItem>
                    {regions.map(r => (
                      <SelectItem key={r.id} value={r.name}>{r.name}{r.dealer_count != null && ` (${r.dealer_count})`}</SelectItem>
                    ))}
                    <SelectItem value="__add_new__" className="text-[#1a224f] font-medium">+ Yeni Bolge Olustur</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">Var olan bolgelerden secin veya yeni bir bolge olusturun</p>
              </div>

              {personnel.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sorumlu Personel</label>
                  <Select value={form.assigned_personnel_id || '_none_'} onValueChange={(val) => setForm({ ...form, assigned_personnel_id: val === '_none_' ? '' : val })}>
                    <SelectTrigger className="w-full h-10 border-gray-200 rounded-lg text-sm">
                      <SelectValue placeholder="Seçiniz" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Seçiniz</SelectItem>
                      {personnel.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Bu bayi sadece atanan personel tarafından görülebilir</p>
                </div>
              )}
            </form>

            {/* Sticky Action Footer - Her zaman görünür, mobil bottom-nav üstünde */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-2 shrink-0" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 sm:flex-initial px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                data-testid="cancel-dealer-btn"
              >
                İptal
              </button>
              <button
                type="submit"
                form="dealer-form"
                disabled={saving}
                className="flex-1 px-4 py-2.5 bg-[#1a224f] text-white rounded-lg text-sm font-medium hover:bg-[#12173a] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors"
                data-testid="save-dealer-btn"
              >
                {saving ? (
                  <><Loader2 className="w-4 h-4 animate-spin" />Kaydediliyor...</>
                ) : (
                  <><Save className="w-4 h-4" />{editingDealer ? 'Güncelle' : 'Kaydet'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Import Modal ===== */}
      {showImportModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 pb-20 sm:pb-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="import-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Upload className="w-5 h-5 text-gray-700" />Musterileri Ice Aktar</h2>
              <button onClick={() => setShowImportModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700 font-medium mb-1">1. Sablonu indirin ve doldurun</p>
                <p className="text-xs text-gray-500 mb-2">Zorunlu alanlar: Email, Sifre, Ad Soyad. Sifre girmezseniz otomatik <code className="bg-white px-1 rounded">Bayi123!</code> atanir.</p>
                <button onClick={handleDownloadTemplate} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-50" data-testid="download-template-btn">
                  <Download className="w-3.5 h-3.5" />Excel Sablonunu Indir
                </button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-sm text-gray-700 font-medium mb-2">2. Doldurdugunuz dosyayi secin (.xlsx veya .csv)</p>
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                  className="hidden"
                  data-testid="import-file-input"
                />
                <button onClick={() => importFileRef.current?.click()} className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-gray-400 hover:bg-white transition-colors">
                  {importFile ? (
                    <span className="flex items-center justify-center gap-2"><FileText className="w-4 h-4" />{importFile.name}</span>
                  ) : 'Dosya secmek icin tiklayin'}
                </button>
              </div>

              {importResult && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-green-50 border border-green-100 rounded-lg p-2 text-center">
                      <p className="text-xs text-green-700">Eklendi</p>
                      <p className="text-lg font-bold text-green-700">{importResult.imported}</p>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded-lg p-2 text-center">
                      <p className="text-xs text-amber-700">Atlandi</p>
                      <p className="text-lg font-bold text-amber-700">{importResult.skipped_count}</p>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded-lg p-2 text-center">
                      <p className="text-xs text-red-700">Hata</p>
                      <p className="text-lg font-bold text-red-700">{importResult.error_count}</p>
                    </div>
                  </div>
                  {(importResult.skipped?.length > 0 || importResult.errors?.length > 0) && (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1 text-gray-500">Satir</th>
                            <th className="text-left px-2 py-1 text-gray-500">Email</th>
                            <th className="text-left px-2 py-1 text-gray-500">Sebep</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(importResult.skipped || []).map((s, i) => (
                            <tr key={`s${i}`} className="border-t border-gray-100">
                              <td className="px-2 py-1 text-gray-600">{s.row}</td>
                              <td className="px-2 py-1 text-gray-700">{s.email}</td>
                              <td className="px-2 py-1 text-amber-700">{s.reason}</td>
                            </tr>
                          ))}
                          {(importResult.errors || []).map((s, i) => (
                            <tr key={`e${i}`} className="border-t border-gray-100">
                              <td className="px-2 py-1 text-gray-600">{s.row}</td>
                              <td className="px-2 py-1 text-gray-700">{s.email || '-'}</td>
                              <td className="px-2 py-1 text-red-700">{s.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button onClick={() => setShowImportModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm font-medium">Kapat</button>
                <button onClick={handleImport} disabled={importing || !importFile} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2" data-testid="import-submit-btn">
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {importing ? 'Yukleniyor...' : 'Ice Aktar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== Bulk Group (Bolge) Modal ===== */}
      {showBulkGroupModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5 space-y-4" data-testid="bulk-group-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Bolgeye Ata ({selectedRows.length})</h3>
              <button onClick={() => setShowBulkGroupModal(false)} className="p-1.5 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-gray-600">Secili bayilere atanacak bolgeyi secin. Var olanlardan secebilir veya yeni bolge olusturabilirsiniz.</p>
            <Select
              value={bulkGroupValue || '_none_'}
              onValueChange={async (v) => {
                if (v === '__add_new__') {
                  const newName = window.prompt('Yeni bolge adi:');
                  if (newName && newName.trim()) {
                    const created = await createRegion(newName);
                    if (created) {
                      setBulkGroupValue(created.name);
                      toast.success(`"${created.name}" olusturuldu`);
                    }
                  }
                  return;
                }
                setBulkGroupValue(v === '_none_' ? '' : v);
              }}
            >
              <SelectTrigger className="w-full h-10 border-gray-200 rounded-lg text-sm" data-testid="bulk-region-select"><SelectValue placeholder="Bolge seciniz" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="_none_">Bolgesiz (temizle)</SelectItem>
                {regions.map(r => (
                  <SelectItem key={r.id} value={r.name}>{r.name}{r.dealer_count != null && ` (${r.dealer_count})`}</SelectItem>
                ))}
                <SelectItem value="__add_new__" className="text-[#1a224f] font-medium">+ Yeni Bolge Olustur</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowBulkGroupModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm">Iptal</button>
              <button onClick={() => runBulkAction('group', bulkGroupValue)} disabled={bulkProcessing} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50" data-testid="bulk-group-submit">Uygula</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Bulk Personnel Modal ===== */}
      {showBulkPersonnelModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5 space-y-4" data-testid="bulk-personnel-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Personel Ata ({selectedRows.length})</h3>
              <button onClick={() => setShowBulkPersonnelModal(false)} className="p-1.5 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-gray-600">Secili bayilere sorumlu personel atayin.</p>
            <Select value={bulkPersonnelId || '_none_'} onValueChange={(v) => setBulkPersonnelId(v === '_none_' ? '' : v)}>
              <SelectTrigger className="w-full h-10 border-gray-200 rounded-lg text-sm" data-testid="bulk-personnel-select"><SelectValue placeholder="Personel Secin" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">Atamayi kaldir</SelectItem>
                {personnel.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowBulkPersonnelModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm">Iptal</button>
              <button onClick={() => runBulkAction('assign_personnel', bulkPersonnelId)} disabled={bulkProcessing} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50" data-testid="bulk-personnel-submit">Uygula</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Region Manager Modal ===== */}
      {showRegionManager && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 pb-20 sm:pb-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto" data-testid="region-manager-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2"><RegionIcon className="w-5 h-5 text-gray-700" />Bolgeleri Yonet</h3>
              <button onClick={() => setShowRegionManager(false)} className="p-1.5 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <button
                onClick={async () => {
                  const newName = window.prompt('Yeni bolge adi:');
                  if (newName && newName.trim()) {
                    const created = await createRegion(newName);
                    if (created) toast.success(`"${created.name}" olusturuldu`);
                  }
                }}
                className="w-full px-3 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 flex items-center justify-center gap-1.5"
                data-testid="region-create-btn"
              >
                <Plus className="w-4 h-4" />Yeni Bolge Olustur
              </button>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                {regions.length === 0 ? (
                  <p className="p-6 text-center text-sm text-gray-500">Henuz bolge tanimlanmamis</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {regions.map(r => (
                      <li key={r.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">{r.name}</p>
                          <p className="text-[11px] text-gray-500">{r.dealer_count || 0} bayi</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button onClick={() => renameRegion(r)} className="p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900 rounded" title="Yeniden adlandir" data-testid={`region-rename-${r.id}`}>
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => deleteRegion(r.id)} className="p-1.5 text-red-500 hover:bg-[#eef0f7] rounded" title="Sil" data-testid={`region-delete-${r.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
